/** Things you control: lights, power, energy, climate, openings, security, media, network. */
import { c, dot, rr, type Glyph } from './geom.js';

// ---- shared silhouettes
const BULB = 'M9 17v-.6c0-1.8-3-3.3-3-6.9a6 6 0 0 1 12 0c0 3.6-3 5.1-3 6.9v.6z';
const PENDANT_SHADE = 'M5.5 15a6.5 6.5 0 0 1 13 0z';
const LAMP_SHADE = 'M8 3.5h8l2.5 7.5h-13z';
const LANTERN = 'M8.5 8.5h7v9h-7z';
const SPOT = 'M7 8a5 5 0 0 0 10 0z';
const FLUSH = 'M4 6.5h16a8 6 0 0 1-16 0z';
const DOWN = `${c(5, 9.5, 2)}${c(12, 9.5, 2)}${c(19, 9.5, 2)}`;
const flame = (x: number): string => `M${x} 7.6c-1-.7-1-2 0-3 1 1 1 2.3 0 3z`;
const FLAMES = [4, 8.7, 15.3, 20].map(flame).join('');
const CHANDELIER = `M12 2.5v6.5M4 9.5c0 3 3.5 5 8 5s8-2 8-5M8.7 9.5c0 1.9 1.4 3 3.3 3s3.3-1.1 3.3-3M12 14.5v3${dot(12, 20)}${FLAMES}`;
const BEDSIDE_SHADE = 'M8.5 3h7l2 5h-11z';
const BEDSIDE = `${BEDSIDE_SHADE}M12 8v3.5${rr(4, 11.5, 16, 7, 1.5)}M10 15h4M6 18.5v2.5M18 18.5v2.5`;
const FLOOR_SHADE = 'M13.5 11.5a4 4 0 0 1 8 0z';
const FLOOR_LAMP = `M9.5 21V8.5a4.5 4.5 0 0 1 4.5-4.5h1a2.5 2.5 0 0 1 2.5 2.5v1${FLOOR_SHADE}M6.5 21h6`;
const DESK_SHADE = 'M12 7.5 14.5 5l6 3.5-5 5z';
const DESK_LAMP = `M4.5 21h9M9 21 6.5 13.5l6-5.5${DESK_SHADE}`;
const SCONCE_SHADE = 'M9 9.5h11l-2 6h-7z';
const SCONCE = `${rr(3, 7.5, 3, 10, 1)}M6 12.5h4${SCONCE_SHADE}`;
const MIRROR_GLASS = rr(6, 6, 12, 15, 2.5);
const MIRROR = `${MIRROR_GLASS}M8 3h8M9.5 11.5l3-3M9.5 15.5l5-5`;
const CABINET_CONE = 'M5.5 13.5h13l2.5 7h-18z';
const CABINET = `${rr(3, 3, 18, 8, 1.5)}M12 3v8${dot(10, 7.5)}${dot(14, 7.5)}M5 13.5h14`;
const SKY_PANEL = `${rr(4, 3.5, 16, 9, 1.5)}M12 3.5v9M4 8h16`;
const NIGHT_BODY = rr(6.5, 3, 11, 15.5, 5.5);
const NIGHTLIGHT = `${NIGHT_BODY}M13.2 7.5a3.5 3.5 0 1 0 2.3 5.6 3 3 0 0 1-2.3-5.6zM10 21h4`;
const STEPS = `M3 20h5v-5h5v-5h5V5h3M5.5 17.5h1M10.5 12.5h1M15.5 7.5h1`;
const PLUG_BODY = 'M6 8h12v3a6 6 0 0 1-12 0z';
const SOCKET = rr(3, 3, 18, 18, 4);
const SWITCH_ROCKER = rr(9, 7, 6, 10, 1.5);
const BATTERY = `${rr(2.5, 7, 17, 10, 2.5)}M21.5 10.5v3`;
const batt = (w: number) => rr(4.75, 9.25, w, 5.5, 0.9);
const DIAL = c(12, 12, 9);
const RADIATOR = rr(3, 5, 18, 13, 2.5);
const AC_UNIT = rr(2.5, 4, 19, 8, 2.5);
const FAN_BLADES = 'M12 10c0-4 1-6 3-6s2 3-1 5M14 12c4 0 6 1 6 3s-3 2-5-1M12 14c0 4-1 6-3 6s-2-3 1-5M10 12c-4 0-6-1-6-3s3-2 5 1';
const SHIELD = 'M12 3 5 6v5.5c0 4.4 2.9 7.9 7 9.5 4.1-1.6 7-5.1 7-9.5V6z';
const LOCK_BODY = rr(4, 10, 16, 11, 2.5);
const WIN_FRAME = rr(4, 3, 16, 18, 1.5);
const BLIND_FRAME = 'M3 4h18M5 4v16.5M19 4v16.5M3.5 20.5h17';
const SHUTTER_BOX = `${rr(3, 3, 18, 4, 1)}M5 7v13.5M19 7v13.5M3.5 20.5h17`;
const CURTAIN_RAIL = 'M3 3.5h18';
const ROOF = 'M3 18l5.5-10h12.5L15.5 18z';
const ROOF_PANE = 'M7 16l3.8-6.5h7.4L14.4 16z';
const TV = `${rr(3, 4, 18, 12, 2)}M8 20h8M12 16v4`;
const SPEAKER = `${rr(6, 3, 12, 18, 2.5)}${c(12, 14, 3)}${dot(12, 7.5)}`;
const WIFI_ARCS = ['M8.5 16a6 6 0 0 1 7 0', 'M5.5 12.5a10.5 10.5 0 0 1 13 0', 'M2.5 9a15 15 0 0 1 19 0'];
const wifi = (lvl: number): Glyph => ({
  d: `${dot(12, 19.5)}${WIFI_ARCS.slice(0, lvl).join('')}`,
  ...(lvl < 3 ? { x: WIFI_ARCS.slice(lvl).join('') } : {}),
});
const BARS = ['M5 19v-3', 'M9.5 19v-6', 'M14 19v-9', 'M18.5 19V7'];
const signal = (lvl: number): Glyph => ({
  d: BARS.slice(0, Math.max(lvl, 1)).join(''),
  ...(lvl < 4 ? { x: BARS.slice(Math.max(lvl, 1)).join('') } : {}),
});

export const DEVICES: Record<string, Glyph> = {
  // ---- lights (on = the lit part takes the soft fill, plus short rays where the light falls).
  // One glyph per fitting type so a room with many lights reads at a glance (lib/icons/lights.ts).
  bulb: `${BULB}M10 20.5h4`,
  'bulb-on': { d: `${BULB}M10 20.5h4`, g: BULB },
  // flush ceiling fitting
  ceiling: `M3 4.5h18${FLUSH}${dot(12, 14.5)}`,
  'ceiling-on': { d: `M3 4.5h18${FLUSH}${dot(12, 14.5)}M12 17v3M6.5 15 5 17.5M17.5 15l1.5 2.5`, g: FLUSH },
  // recessed downlights: a row of small circles
  downlights: `M3 4.5h18${DOWN}`,
  'downlights-on': { d: `M3 4.5h18${DOWN}M5 14v3.5M12 14v3.5M19 14v3.5`, g: DOWN },
  pendant: `M12 3v5.5${PENDANT_SHADE}M10 15a2 2 0 0 0 4 0`,
  'pendant-on': {
    d: `M12 3v5.5${PENDANT_SHADE}M10 15a2 2 0 0 0 4 0M12 19.5v1.5M7.5 18.5l-1 1.2M16.5 18.5l1 1.2`,
    g: `${PENDANT_SHADE}M10 15a2 2 0 0 0 4 0z`,
  },
  chandelier: CHANDELIER,
  'chandelier-on': { d: CHANDELIER, f: FLAMES },
  // table / side lamp
  lamp: `${LAMP_SHADE}M12 11v9.5M8.5 20.5h7`,
  'lamp-on': { d: `${LAMP_SHADE}M12 11v9.5M8.5 20.5h7`, g: LAMP_SHADE },
  // lamp on a nightstand
  bedside: BEDSIDE,
  'bedside-on': { d: `${BEDSIDE}M5.5 6.5l-1.5-.5M18.5 6.5l1.5-.5`, g: BEDSIDE_SHADE },
  // arc floor lamp
  'floor-lamp': FLOOR_LAMP,
  'floor-lamp-on': { d: `${FLOOR_LAMP}M17.5 14v2.5M15 13.5l-1.2 2M20 13.5l1.2 2`, g: FLOOR_SHADE },
  'desk-lamp': DESK_LAMP,
  'desk-lamp-on': { d: `${DESK_LAMP}M19 13.5l2 2M16.5 15.5l1 2.2M21 10.5l1 .5`, g: DESK_SHADE },
  // wall light / sconce (up-lighter on a backplate)
  sconce: SCONCE,
  'sconce-on': { d: `${SCONCE}M14.5 6.5V4M10.5 6.8 9.5 4.6M18.5 6.8l1-2.2`, g: SCONCE_SHADE },
  spot: `M3.5 8h17${SPOT}`,
  'spot-on': { d: `M3.5 8h17${SPOT}M12 16v3M7.5 15l-1.5 2.5M16.5 15l1.5 2.5`, g: SPOT },
  lantern: `M12 3v2.5M8.5 8.5 10 5.5h4l1.5 3${LANTERN}M7.5 17.5h9M12 17.5V21`,
  'lantern-on': { d: `M12 3v2.5M8.5 8.5 10 5.5h4l1.5 3${LANTERN}M7.5 17.5h9M12 17.5V21`, g: LANTERN },
  strip: `${rr(2.5, 9.5, 19, 5, 2.5)}${dot(7, 12)}${dot(12, 12)}${dot(17, 12)}`,
  'strip-on': {
    d: `${rr(2.5, 9.5, 19, 5, 2.5)}${dot(7, 12)}${dot(12, 12)}${dot(17, 12)}M7 4.5v2M12 4v2.5M17 4.5v2`,
    g: rr(2.5, 9.5, 19, 5, 2.5),
  },
  // mirror with a light bar
  'mirror-light': MIRROR,
  'mirror-light-on': { d: `${MIRROR}M5.5 3.5 4 2.5M18.5 3.5l1.5-1`, g: MIRROR_GLASS },
  // under-cabinet light
  'cabinet-light': CABINET,
  'cabinet-light-on': { d: `${CABINET}M7 16l-1 3M12 16v3.5M17 16l1 3`, g: CABINET_CONE },
  // a skylight-style ceiling panel
  'skylight-light': SKY_PANEL,
  'skylight-light-on': { d: `${SKY_PANEL}M8 15l-1 3M12 15v3.5M16 15l1 3`, g: rr(4, 3.5, 16, 9, 1.5) },
  nightlight: NIGHTLIGHT,
  'nightlight-on': { d: `${NIGHTLIGHT}M3.5 11h1.5M19 11h1.5M4.5 6l1.2.8M19.5 6l-1.2.8`, g: NIGHT_BODY },
  // stair / step lights
  'step-light': STEPS,
  'step-light-on': { d: STEPS, f: `${c(6, 17.5, 1.1)}${c(11, 12.5, 1.1)}${c(16, 7.5, 1.1)}`, g: 'M3 20h5v-5h5v-5h5V5h3v15z' },

  // ---- power
  power: 'M12 3v9M7 6a7.5 7.5 0 1 0 10 0',
  switch: `${rr(5, 3, 14, 18, 3)}${SWITCH_ROCKER}M9 12h6`,
  'switch-on': {
    d: `${rr(5, 3, 14, 18, 3)}${SWITCH_ROCKER}M9 12h6`,
    g: 'M10.5 7h3A1.5 1.5 0 0 1 15 8.5V12H9V8.5A1.5 1.5 0 0 1 10.5 7z',
  },
  plug: `M9 3v5M15 3v5${PLUG_BODY}M12 17v4`,
  'plug-on': { d: `M9 3v5M15 3v5${PLUG_BODY}M12 17v4`, g: PLUG_BODY },
  socket: `${SOCKET}M12 7.5v3M7.5 15h3M13.5 15h3`,
  'socket-on': { d: `${SOCKET}M12 7.5v3M7.5 15h3M13.5 15h3`, g: SOCKET },
  toggle: { d: rr(2, 7, 20, 10, 5), f: c(16, 12, 3) },
  'toggle-off': `${rr(2, 7, 20, 10, 5)}${c(8, 12, 3)}`,
  button: `${c(12, 12, 8)}${c(12, 12, 3.5)}`,

  // ---- energy
  bolt: 'M13 2 5 14h6l-1 8 8-12h-6z',
  battery: { d: BATTERY, f: batt(9.5) },
  'battery-0': BATTERY,
  'battery-25': { d: BATTERY, f: batt(3.2) },
  'battery-50': { d: BATTERY, f: batt(6.3) },
  'battery-75': { d: BATTERY, f: batt(9.5) },
  'battery-100': { d: BATTERY, f: batt(12.5) },
  'battery-low': { d: `${BATTERY}`, f: batt(2.2) },
  'battery-charging': `${BATTERY}M11.5 8.5 9 12h4l-2.5 3.5`,
  solar: 'M4 14h16l-2-8H6zM5 10h14M10 6l-1 8M14 6l1 8M12 14v4M8 21h8',
  pylon: 'M8.5 21 11 3h2l2.5 18M5 6.5h14M6.5 10h11M9.4 14.5h5.2M9.4 14.5 15.5 21M14.6 14.5 8.5 21M5 6.5v2M19 6.5v2',
  inverter: `${rr(3, 3, 18, 18, 3)}M6 18 18 6M6 9c.8-1.3 1.7-1.3 2.5 0s1.7 1.3 2.5 0M13 15.5h5M13 18h5`,
  'ev-charger': 'M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M3.5 21h13M10.5 7 8.5 11h3l-2 4M15 10h1.5a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 0 3 0V9l-2-2',
  meter: `${rr(3, 4, 18, 16, 3)}${rr(6.5, 7.5, 11, 4.5, 1)}${dot(8.5, 16)}${dot(12, 16)}${dot(15.5, 16)}`,
  current: 'M2.5 12c1.6-4.5 3.2-4.5 4.8 0s3.2 4.5 4.8 0 3.2-4.5 4.8 0 3.2 4.5 4.6 0',
  voltage: 'M3 12h6M9 6v12M13.5 9v6M13.5 12H21M16.5 6.5h3M18 5v3',

  // ---- climate (heat/cool = the body takes the soft fill)
  thermostat: `${DIAL}M12 7v5M8 16.5a5.5 5.5 0 0 1 8 0`,
  'thermostat-heat': {
    d: DIAL,
    f: 'M12 7c.6 1.6 3 2.8 3 5.6a3 3 0 0 1-6 0c0-1.4.8-2.3 1.5-2.9 0 1.1.6 1.7 1.2 1.7 0-1.7-.5-3.1.3-4.4z',
  },
  'thermostat-cool': `${DIAL}M12 7.5v9M8.1 9.75l7.8 4.5M15.9 9.75l-7.8 4.5`,
  radiator: `${RADIATOR}M7.5 5v13M12 5v13M16.5 5v13M6 18v2M18 18v2`,
  'radiator-on': { d: `${RADIATOR}M7.5 5v13M12 5v13M16.5 5v13M6 18v2M18 18v2`, g: RADIATOR },
  ufh: `${rr(3, 17.5, 18, 3.5, 1)}M7 14.5c-1.2-1.5 1.2-3 0-4.5S5.8 7 7 5.5M12 14.5c-1.2-1.5 1.2-3 0-4.5s-1.2-3 0-4.5M17 14.5c-1.2-1.5 1.2-3 0-4.5s-1.2-3 0-4.5`,
  'ufh-on': {
    d: `${rr(3, 17.5, 18, 3.5, 1)}M7 14.5c-1.2-1.5 1.2-3 0-4.5S5.8 7 7 5.5M12 14.5c-1.2-1.5 1.2-3 0-4.5s-1.2-3 0-4.5M17 14.5c-1.2-1.5 1.2-3 0-4.5s-1.2-3 0-4.5`,
    g: rr(3, 17.5, 18, 3.5, 1),
  },
  ac: `${AC_UNIT}M6 9h12`,
  'ac-on': {
    d: `${AC_UNIT}M6 9h12M7 15.5c0 1.5 1.5 1.5 1.5 3M12 15.5c0 1.5 1.5 1.5 1.5 3M17 15.5c0 1.5 1.5 1.5 1.5 3`,
    g: AC_UNIT,
  },
  fan: `${c(12, 12, 2)}${FAN_BLADES}`,
  'fan-on': { d: c(12, 12, 2), spin: FAN_BLADES, f: c(12, 12, 2) },
  flame: 'M12 3c1 3 5 5 5 10a5 5 0 0 1-10 0c0-2.5 1.5-4 2.5-5 0 2 1 3 2 3 0-3-1-5.5.5-8z',
  snowflake: 'M12 2.5v19M3.8 7.25l16.4 9.5M20.2 7.25 3.8 16.75M9 4l3 2.5L15 4M9 20l3-2.5 3 2.5',
  'water-heater': `${rr(6, 3, 12, 18, 3)}M10 15c0-1.5 2-2 2-4 0 2 2 2.5 2 4a2 2 0 0 1-4 0z`,
  damper: `${c(12, 12, 8.5)}M6.5 17.5l11-11${dot(12, 12)}`,
  duct: 'M3 6.5h18M3 17.5h18M6 12h10M13 9l3 3-3 3',
  filter: `${rr(3, 4, 18, 16, 2)}M6 8l2 8 2-8 2 8 2-8 2 8 2-8`,
  pump: `${c(11, 13, 6)}M11 7V3.5h5M17 13h4M11 13l3-3M6 21h10`,
  valve: 'M3 10h5v6H3zM16 10h5v6h-5zM8 13h8M12 13V7M9 5h6',
  tap: 'M8 21h8M12 21v-6M6 9h6V6a3 3 0 0 1 6 0v1M6 9v2h6V9',
  // pop-up sprinkler: head on a riser in the ground, three arcs of spray
  sprinkler: `M4 21h16M12 21v-6M10 15h4M8 12a5.5 5.5 0 0 1 8 0M5 8.5a9.5 9.5 0 0 1 14 0${dot(12, 3)}`,
  vacuum: `${c(12, 12, 9)}${c(12, 12, 3)}M7 6.5l2 1.5M17 6.5l-2 1.5`,
  'vacuum-on': { d: `${c(12, 12, 9)}${c(12, 12, 3)}M7 6.5l2 1.5M17 6.5l-2 1.5`, g: c(12, 12, 9) },

  // ---- openings (open = daylight, the soft fill)
  window: `${WIN_FRAME}M12 3v18M4 12h16`,
  'window-open': {
    d: `${WIN_FRAME}M12 3v18M4 12h8M12 3l5 3v12l-5 3`,
    g: 'M12 3h6.5A1.5 1.5 0 0 1 20 4.5v15a1.5 1.5 0 0 1-1.5 1.5H12z',
  },
  blind: `${BLIND_FRAME}M5 8h14M5 12h14M5 16h14`,
  'blind-50': { d: `${BLIND_FRAME}M5 8h14M5 12h14`, g: 'M5 12h14v8.5H5z' },
  'blind-open': { d: `${BLIND_FRAME}M5 6.5h14`, g: 'M5 6.5h14v14H5z' },
  shutter: `${SHUTTER_BOX}M5 10.5h14M5 14h14M5 17.5h14`,
  'shutter-50': { d: `${SHUTTER_BOX}M5 10.5h14M5 13.5h14`, g: 'M5 13.5h14v7H5z' },
  'shutter-open': { d: SHUTTER_BOX, g: 'M5 7h14v13.5H5z' },
  curtain: `${CURTAIN_RAIL}M4.5 4v16.5h7V4M12.5 4v16.5h7V4M8 6v14.5M16 6v14.5`,
  'curtain-open': {
    d: `${CURTAIN_RAIL}M4 4v16.5h2.5c0-3 1-4.5 2.5-6.5C7.5 12 6.5 9 6.5 4M20 4v16.5h-2.5c0-3-1-4.5-2.5-6.5 1.5-2 2.5-5 2.5-10`,
    g: 'M8.5 4h7v16.5h-7z',
  },
  skylight: `${ROOF}${ROOF_PANE}`,
  'skylight-open': { d: `${ROOF}M7 16l3.8-6.5M10.8 9.5 13.5 4h7.4l-2.7 5.5`, g: ROOF_PANE },

  // ---- security & access
  lock: `${LOCK_BODY}M8 10V7a4 4 0 0 1 8 0v3M12 14.5v2`,
  unlock: `${LOCK_BODY}M8 10V7a4 4 0 0 1 7.4-2M12 14.5v2`,
  'lock-jammed': { d: `${LOCK_BODY}M8 10V7a4 4 0 0 1 8 0v3M12 13v3.5${dot(12, 18.5)}`, g: LOCK_BODY },
  key: `${c(8, 15.5, 4.5)}M11.2 12.3 20 3.5M16.5 7l2.5 2.5M14.5 9l2 2`,
  shield: SHIELD,
  'shield-check': { d: `${SHIELD}M9 12l2 2 4-4`, g: SHIELD },
  'shield-off': `${SHIELD}M9.5 9.5l5 5`,
  'shield-alert': { d: `${SHIELD}M12 8v5${dot(12, 16)}`, g: SHIELD },
  'shield-pending': `${SHIELD}M12 8.5V12l2 1.5`,
  'alarm-panel': `${rr(5, 3, 14, 18, 2.5)}M8.5 6.5h7${dot(9, 11)}${dot(12, 11)}${dot(15, 11)}${dot(9, 14)}${dot(12, 14)}${dot(15, 14)}${dot(9, 17)}${dot(12, 17)}${dot(15, 17)}`,
  camera: `M3 8a2 2 0 0 1 2-2h3l1.5-2h5L16 6h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z${c(12, 12.5, 3.5)}`,
  cctv: 'M3 7l13 5-2 4-13-5zM16 12l3 1M19 9v8M5 13l-1 6',
  siren: 'M7 18v-6a5 5 0 0 1 10 0v6M5 18h14v3H5z',
  'siren-on': { d: 'M7 18v-6a5 5 0 0 1 10 0v6M5 18h14v3H5zM12 2.5V4.5M4.5 6l1.5 1.5M19.5 6 18 7.5', g: 'M7 18v-6a5 5 0 0 1 10 0v6z' },
  bell: 'M6 16v-5a6 6 0 0 1 12 0v5l2 2H4zM10 20.5a2 2 0 0 0 4 0',
  'bell-ring': {
    d: 'M6 16v-5a6 6 0 0 1 12 0v5l2 2H4zM10 20.5a2 2 0 0 0 4 0M2.5 8a8 8 0 0 1 2.5-4.5M21.5 8A8 8 0 0 0 19 3.5',
    g: 'M6 16v-5a6 6 0 0 1 12 0v5l2 2H4z',
  },
  doorbell: `${rr(7, 2.5, 10, 19, 3)}${c(12, 14, 2.5)}M10.5 6.5h3`,
  'doorbell-ring': { d: `${rr(7, 2.5, 10, 19, 3)}${c(12, 14, 2.5)}M10.5 6.5h3M3.5 11a8 8 0 0 0 0 6M20.5 11a8 8 0 0 1 0 6`, f: c(12, 14, 2.5) },
  intercom: `${rr(6, 3, 12, 18, 2)}${c(12, 9, 2.5)}M9.5 15h5M9.5 17.5h5`,
  smoke: `${c(12, 12, 8.5)}${c(12, 12, 3)}M12 4v1M12 19v1`,
  'smoke-on': { d: `${c(12, 12, 8.5)}${c(12, 12, 3)}M12 4v1M12 19v1`, g: c(12, 12, 8.5) },
  gas: 'M8 7h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM10 7V4.5h4V7M9.5 3.5h5M6 12h12',

  // ---- media
  speaker: SPEAKER,
  'speaker-on': { d: SPEAKER, g: c(12, 14, 3) },
  tv: TV,
  'tv-on': { d: TV, g: rr(3, 4, 18, 12, 2) },
  'tv-box': `${rr(3, 9, 18, 7, 2)}${dot(6.5, 12.5)}M14 12.5h3.5M6 16v2M18 16v2`,
  remote: `${rr(8, 2.5, 8, 19, 3)}${c(12, 7.5, 1.75)}${dot(10.5, 12.5)}${dot(13.5, 12.5)}${dot(10.5, 15.5)}${dot(13.5, 15.5)}`,
  music: `M9 18V5l11-2v13${c(6.5, 18, 2.5)}${c(17.5, 16, 2.5)}`,
  microphone: `${rr(9, 3, 6, 11, 3)}M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6`,

  // ---- network & computing
  wifi: wifi(3),
  'wifi-1': wifi(1),
  'wifi-2': wifi(2),
  'wifi-3': wifi(3),
  'wifi-off': `M3 3l18 18M8.5 16a6 6 0 0 1 7 0${dot(12, 19.5)}`,
  signal: signal(4),
  'signal-1': signal(1),
  'signal-2': signal(2),
  'signal-3': signal(3),
  'signal-4': signal(4),
  router: `${rr(3, 13, 18, 7, 2)}${dot(7, 16.5)}${dot(11, 16.5)}M17 13V9M8.5 7a5 5 0 0 1 7 0M6 4.5a8.5 8.5 0 0 1 12 0`,
  satellite: 'M5 13a6 6 0 0 0 6 6M5 19l4-4m2-8 6 6M13 5l2-2 6 6-2 2M14 10l3-3',
  dish: `M5 9a9.5 9.5 0 0 0 10 10zM10 14l5-5${dot(15.5, 8.5)}M9 18.5 7.5 21h6`,
  server: `${rr(4, 3, 16, 7.5, 2)}${rr(4, 13.5, 16, 7.5, 2)}${dot(8, 6.75)}${dot(8, 17.25)}M12 6.75h4M12 17.25h4`,
  phone: `${rr(6.5, 2.5, 11, 19, 2.5)}M11 18.5h2`,
  tablet: `${rr(4, 3, 16, 18, 2.5)}M11 18h2`,
  device: `${rr(7, 2, 10, 20, 2.5)}M11 18.5h2`,
  laptop: `${rr(4, 5, 16, 11, 1.5)}M2 19h20`,
  chip: `${rr(6, 6, 12, 12, 2)}M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4`,
  database: 'M5 6c0-1.7 3.1-3 7-3s7 1.3 7 3-3.1 3-7 3-7-1.3-7-3zM5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3',
  storage: 'M8 3h6.5L18 6.5V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2zM9.5 3v3.5M12 3v3.5M14.5 3.5v3',
  sim: `M8 3h6.5L18 6.5V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2z${rr(9, 11, 6, 6.5, 1)}M12 11v6.5`,
  ethernet: 'M4 8h16v10H4zM8 18v-3M11 18v-3M13 18v-3M16 18v-3M9 8V5.5h6V8',
  bluetooth: 'M7 7.5l10 9-5 4.5V3l5 4.5-10 9',
  globe: `${c(12, 12, 9)}M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18`,
  printer: 'M7 8V3.5h10V8M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M7 14h10v6.5H7z',
  robot: `${rr(4, 8, 16, 12, 3)}M12 4v4M9 13v1M15 13v1M9 17h6`,
};
